import { FC, useCallback, useEffect } from 'react';

import { getFormattedIntNumber, sortEmployerUsers, sortEntities } from 'utils/clickme';
import {
    ACCOUNT_COLUMN_SIZE,
    ACCOUNTS_CONTROL_HEADERS,
    ACCOUNTS_CONTROL_SORT_HEADERS,
    AccountsControlHeader,
} from 'utils/constants/accounts';
import { NON_BREAKING_SPACE } from 'utils/constants/common';
import { VIEW_DATE_FORMAT } from 'utils/constants/date';
import { formatDate } from 'utils/date';

import accountsControlEditButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/accounts_control/accounts_control_edit_button_click';
import {
    Button,
    Card,
    FullPageContainer,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
    Text,
    useBreakpoint,
    VSpacing,
    VSpacingContainer,
} from '@hh.ru/magritte-ui';
import { HeadersTypography, TextTypography } from '@hh.ru/magritte-ui-typography/types';
import { PenOutlinedSize16 } from '@hh.ru/magritte-ui/icon';
import { Link as SPALink } from '@hh.ru/redux-spa-middleware';

import useTrls from 'hooks/translation/useTrls';

import { LabelSortMagritte } from 'components/AccountsControl/components/LabelSortMagritte';
import { UsersList } from 'components/AccountsControl/components/UsersList';
import { fetchAccountsBalance } from 'components/AccountsControl/fetchAccountsBalance';

import { Account, setAccountsAccounts, setAccountsSorts } from 'store/models/accounts';
import { useDispatch, useSelector } from 'store/utils';

const TABLE_CELL_TYPOGRAPHY: TextTypography | HeadersTypography = 'paragraph-3-regular';

export const AccountsControl: FC = () => {
    const { accounts, balance, sorts } = useSelector((state) => state.accounts);

    const dispatch = useDispatch();
    const { isGtS } = useBreakpoint();
    const trl = useTrls();

    useEffect(() => {
        void dispatch(fetchAccountsBalance());
    }, [dispatch]);

    const onClickHeadCell = useCallback(
        (field: AccountsControlHeader) => {
            const { newItems, newSorts } = sortEntities(accounts, sorts, field as keyof Account);
            dispatch([setAccountsSorts(newSorts), setAccountsAccounts(newItems)]);
        },
        [dispatch, accounts, sorts]
    );

    return (
        <>
            <FullPageContainer>
                {({ paddingLeft, paddingRight }) => (
                    <Card stretched>
                        <Table
                            divider
                            virtualized={false}
                            cellsGap={24}
                            paddingLeft={paddingLeft}
                            paddingRight={paddingRight}
                        >
                            <TableHeader>
                                <TableRow align="left">
                                    <TableCell Element="th" />
                                    {ACCOUNTS_CONTROL_HEADERS.map((header) => (
                                        <TableCell key={header} Element="th">
                                            {ACCOUNTS_CONTROL_SORT_HEADERS.includes(header) ? (
                                                <LabelSortMagritte
                                                    columnKey={header}
                                                    label={trl(`accounts.control.header.${header}`)}
                                                    typography="label-3-regular"
                                                    sorts={sorts}
                                                    onClick={() => onClickHeadCell(header)}
                                                />
                                            ) : (
                                                <Text typography="label-3-regular" style="secondary">
                                                    {trl(`accounts.control.header.${header}`)}
                                                </Text>
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {accounts.map(({ id, createdAt, expiredDate, title, users }) => (
                                    <TableRow key={id}>
                                        <TableCell data-qa="table-cell-action" width={50}>
                                            <div>
                                                <Button
                                                    Element={SPALink}
                                                    to={`/new/account/${id}`}
                                                    onClick={() => accountsControlEditButtonClick()}
                                                    data-qa="edit-cabinet"
                                                    size="small"
                                                    mode="secondary"
                                                    style="neutral"
                                                    icon={<PenOutlinedSize16 />}
                                                />
                                            </div>
                                        </TableCell>
                                        {ACCOUNTS_CONTROL_HEADERS.map((header) => (
                                            <TableCell
                                                key={header}
                                                data-qa={`table-cell-${header}`}
                                                {...ACCOUNT_COLUMN_SIZE[header]}
                                            >
                                                {header === AccountsControlHeader.CreatedAt && (
                                                    <Text typography={TABLE_CELL_TYPOGRAPHY}>
                                                        {formatDate(createdAt, VIEW_DATE_FORMAT)}
                                                    </Text>
                                                )}
                                                {header === AccountsControlHeader.ExpiredDate && expiredDate && (
                                                    <Text typography={TABLE_CELL_TYPOGRAPHY}>
                                                        {formatDate(expiredDate, VIEW_DATE_FORMAT)}
                                                    </Text>
                                                )}
                                                {header === AccountsControlHeader.Accounts && (
                                                    <VSpacingContainer default={8}>
                                                        <Text typography={TABLE_CELL_TYPOGRAPHY}>
                                                            {trl('id.space', [id])}
                                                        </Text>
                                                        <Text typography={TABLE_CELL_TYPOGRAPHY}>{title}</Text>
                                                    </VSpacingContainer>
                                                )}
                                                {header === AccountsControlHeader.Users && (
                                                    <UsersList
                                                        users={sortEmployerUsers(users)}
                                                        textTypography={TABLE_CELL_TYPOGRAPHY}
                                                    />
                                                )}
                                                {header === AccountsControlHeader.RealBalance && (
                                                    <>
                                                        {balance[id]?.realBalance >= 0 ? (
                                                            <Text typography={TABLE_CELL_TYPOGRAPHY}>
                                                                {getFormattedIntNumber(balance[id].realBalance)}
                                                                {NON_BREAKING_SPACE}
                                                                {trl('currency')}
                                                            </Text>
                                                        ) : (
                                                            <Skeleton height={24} width={50} loading />
                                                        )}
                                                    </>
                                                )}
                                                {header === AccountsControlHeader.BonusBalance && (
                                                    <>
                                                        {balance[id]?.bonusBalance >= 0 ? (
                                                            <Text typography={TABLE_CELL_TYPOGRAPHY}>
                                                                {getFormattedIntNumber(balance[id].bonusBalance)}
                                                            </Text>
                                                        ) : (
                                                            <Skeleton height={24} width={50} loading />
                                                        )}
                                                    </>
                                                )}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Card>
                )}
            </FullPageContainer>
            {!isGtS && <Text>{trl('accounts.control.warning.mobile')}</Text>}
            <VSpacing default={48} />
        </>
    );
};
